/**
 * Geo-based currency detection for pricing.
 * 
 * Rules (from GEO_PRICING_DESIGN.md):
 *   UK (GB) → GBP
 *   Czechia (CZ) → CZK  (EU member, own currency)
 *   Denmark (DK) → DKK  (EU member, own currency)
 *   Japan (JP) → JPY
 *   Kazakhstan (KZ) → KZT
 *   EU 27 member states (remainder) → EUR
 *   All other countries → USD (default/fallback)
 * 
 * Caches result in localStorage for 1 hour to avoid repeated API calls.
 */

const EU_COUNTRY_CODES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
]);

const CACHE_KEY = 'geo_currency_cache';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

function mapCountryToCurrency(countryCode) {
  if (!countryCode) return 'USD';
  const code = countryCode.toUpperCase();
  if (code === 'GB') return 'GBP';
  if (code === 'CZ') return 'CZK';
  if (code === 'DK') return 'DKK';
  if (code === 'JP') return 'JPY';
  if (code === 'KZ') return 'KZT';
  if (EU_COUNTRY_CODES.has(code)) return 'EUR';
  return 'USD';
}

function getCached() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return cached;
  } catch {
    return null;
  }
}

function setCache(countryCode, currency) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      countryCode,
      currency,
      timestamp: Date.now()
    }));
  } catch {
    // localStorage full or unavailable — silent fail
  }
}

/**
 * Detect user's currency based on geo-IP location.
 * Returns { currency: 'GBP'|'EUR'|'USD'|'CZK'|'DKK'|'JPY'|'KZT', countryCode: string }
 * Falls back to USD if detection fails.
 */
export async function detectCurrency() {
  // Check cache first
  const cached = getCached();
  if (cached) {
    return { currency: cached.currency, countryCode: cached.countryCode };
  }

  // Try primary API: ipapi.co (free, no key needed, <1000 req/day)
  try {
    const res = await fetch('https://ipapi.co/json/', { 
      signal: AbortSignal.timeout(4000),
      mode: 'cors'
    });
    if (res.ok) {
      const data = await res.json();
      const countryCode = data.country_code;
      const currency = mapCountryToCurrency(countryCode);
      setCache(countryCode, currency);
      return { currency, countryCode };
    }
  } catch {
    // Primary failed — try fallback
  }

  // Fallback API: ipinfo.io (free, HTTPS, CORS-enabled)
  try {
    const res = await fetch('https://ipinfo.io/json?token=', { 
      signal: AbortSignal.timeout(4000),
      mode: 'cors'
    });
    if (res.ok) {
      const data = await res.json();
      const countryCode = data.country;
      const currency = mapCountryToCurrency(countryCode);
      setCache(countryCode, currency);
      return { currency, countryCode };
    }
  } catch {
    // Both APIs failed
  }

  // Final fallback: USD
  return { currency: 'USD', countryCode: null };
}
